export type CalendarExportPayload = {
  title: string;
  description: string;
  location?: string;
  startTime: Date;
  endTime: Date;
};

const pad = (value: number): string => String(value).padStart(2, "0");

const toIcsDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1);
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const buildGoogleCalendarUrl = ({ title, description, location, startTime, endTime }: CalendarExportPayload): string => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    details: description,
    dates: `${startTime.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}/${endTime.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z")}`,
  });

  if (location) {
    params.set("location", location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildIcsFileContent = ({ title, description, location, startTime, endTime }: CalendarExportPayload): string => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//HandyChat//Media Call Schedule//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${crypto.randomUUID()}`,
    `DTSTAMP:${toIcsDate(new Date())}`,
    `DTSTART:${toIcsDate(startTime)}`,
    `DTEND:${toIcsDate(endTime)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
  ];

  if (location) {
    lines.push(`LOCATION:${escapeIcsText(location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
};

const downloadTextFile = (fileName: string, content: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const fileUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(fileUrl);
};

export const exportCalendarEvent = (payload: CalendarExportPayload, destination: "gmail" | "desktop"): void => {
  if (destination === "gmail") {
    window.open(buildGoogleCalendarUrl(payload), "_blank", "noopener,noreferrer");
    return;
  }

  const icsContent = buildIcsFileContent(payload);
  downloadTextFile("handychat-scheduled-call.ics", icsContent, "text/calendar;charset=utf-8");
};

export const formatLocalScheduleTime = (epochSeconds: number): string => {
  const date = new Date(epochSeconds * 1000);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};
